import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../components/layout/view/ViewHeader";
import { Heading } from "../../../../components/common/heading/Heading";
import Button from "../../../../components/buttons/Button";
import SecondaryPillTabs from "../../../../components/tabs/SecondaryPillTabs";

import StreamVideosGrid from "../../components/grids/StreamVideosGrid";
import CreatorStreamSetupForm from "./forms/CreatorStreamSetupForm";
import StreamLive from "../../components/play/StreamLive";
import StreamReplay from "../../components/play/StreamReplay";

import { useCreatorStreams } from "../../creators/hooks/useCreatorStreams";
import { formatRelativeTime } from "../../../../utils/datetime/formatRelativeTime";

import "./CreatorStreamsView.css";

const getStreamId = (stream) =>
  stream?.streamSessionId || stream?.stream_session_id || null;

const getStatus = (stream) =>
  String(stream?.status || "")
    .trim()
    .toLowerCase();

const getErrorMessage = (error) => {
  if (!error) return "";

  return typeof error === "string"
    ? error
    : error?.response?.data?.message ||
        error?.message ||
        "Unable to load streams.";
};

const formatDateTime = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const getStreamImage = (stream) =>
  stream?.thumbnailUrl ||
  stream?.thumbnail_url ||
  stream?.thumbnail ||
  stream?.imageUrl ||
  stream?.image_url ||
  null;

const getLivePlaybackUrl = (stream) =>
  stream?.ivsPlaybackUrl || stream?.ivs_playback_url || "";

const getReplayPlaybackUrl = (stream) =>
  stream?.vodHlsUrl ||
  stream?.vod_hls_url ||
  stream?.vodMp4Url ||
  stream?.vod_mp4_url ||
  stream?.recordingPlaybackUrl ||
  stream?.recording_playback_url ||
  stream?.replayUrl ||
  stream?.replay_url ||
  "";

const hasReplay = (stream) => Boolean(getReplayPlaybackUrl(stream));

const isReplayStream = (stream) =>
  getStatus(stream) === "ready" || hasReplay(stream);

const canPlayStream = (stream) => {
  if (getStatus(stream) === "live") {
    return Boolean(getLivePlaybackUrl(stream));
  }

  return hasReplay(stream);
};

const getBadgeLabel = (stream) => {
  const status = getStatus(stream);

  if (status === "live") return "Live";
  if (status === "scheduled") return "Scheduled";
  if (isReplayStream(stream)) return "Replay";
  if (status === "ended") return "Ended";
  if (status === "recording_ready") return "Recording";
  if (status === "processing") return "Processing";
  if (status === "failed") return "Failed";

  return "Stream";
};

const getBadgeVariant = (stream) => {
  const status = getStatus(stream);

  if (status === "live") return "live";
  if (status === "scheduled") return "scheduled";
  if (isReplayStream(stream)) return "replay";
  if (status === "ended") return "ended";
  if (status === "recording_ready") return "recording";
  if (status === "processing") return "processing";
  if (status === "failed") return "failed";

  return "default";
};

const getReplayDateText = (stream) => {
  const createdAt =
    stream?.createdAt ||
    stream?.created_at ||
    stream?.actualStart ||
    stream?.actual_start;

  if (!createdAt) return "Replay available";

  return formatRelativeTime(createdAt) || formatDateTime(createdAt);
};

const getStreamDetailText = (stream) => {
  const status = getStatus(stream);

  if (status === "live") return "Streaming now";

  if (status === "scheduled") {
    const scheduledStart = stream?.scheduledStart || stream?.scheduled_start;

    const date = formatDateTime(scheduledStart);

    return date ? `Scheduled ${date}` : "Scheduled";
  }

  if (isReplayStream(stream)) {
    return getReplayDateText(stream);
  }

  if (status === "ended") return "Stream ended";
  if (status === "recording_ready") return "Recording ready";
  if (status === "processing") return "Processing replay";
  if (status === "failed") return "Stream failed";

  return "Open stream";
};

const getInfoModalTitle = (stream) => {
  const status = getStatus(stream);

  if (status === "ended") return "Stream ended";
  if (status === "recording_ready") return "Recording ready";
  if (status === "processing") return "Replay processing";
  if (status === "failed") return "Stream unavailable";

  return stream?.title || "Stream information";
};

const getInfoModalMessage = (stream) => {
  const status = getStatus(stream);

  if (status === "ended") {
    return "This stream has ended and a replay is not available yet.";
  }

  if (status === "recording_ready") {
    return "The recording is ready to be linked and processed as a replay.";
  }

  if (status === "processing") {
    return "The replay is currently being prepared.";
  }

  if (status === "failed") {
    return "This stream could not be loaded.";
  }

  if (status === "ready") {
    return "The replay record is ready, but its playback file is unavailable.";
  }

  return "This stream is not available yet.";
};

const getInfoModalMeta = (stream) => {
  if (getStatus(stream) === "scheduled") {
    return formatDateTime(stream?.scheduledStart || stream?.scheduled_start);
  }

  return formatDateTime(
    stream?.actualEnd ||
      stream?.actual_end ||
      stream?.createdAt ||
      stream?.created_at,
  );
};

export default function CreatorStreamsView({ username }) {
  const [streamFilter, setStreamFilter] = useState("all");
  const [setupStreamId, setSetupStreamId] = useState(null);
  const [liveStreamId, setLiveStreamId] = useState(null);
  const [replayStreamId, setReplayStreamId] = useState(null);
  const [selectedInfoStream, setSelectedInfoStream] = useState(null);

  const { streams, loading, error, getStreamsByUsername, resetCreatorStreams } =
    useCreatorStreams(username);

  const streamList = useMemo(
    () => (Array.isArray(streams) ? streams : []),
    [streams],
  );

  useEffect(() => {
    if (!username) return;

    void getStreamsByUsername();

    return () => {
      resetCreatorStreams();
    };
  }, [username]);

  const counts = useMemo(
    () => ({
      all: streamList.length,
      live: streamList.filter((stream) => getStatus(stream) === "live").length,
      scheduled: streamList.filter(
        (stream) => getStatus(stream) === "scheduled",
      ).length,
      replay: streamList.filter(isReplayStream).length,
    }),
    [streamList],
  );

  const streamFilterTabs = [
    { id: "all", label: "All", count: counts.all },
    { id: "live", label: "Live", count: counts.live },
    { id: "scheduled", label: "Scheduled", count: counts.scheduled },
    { id: "replay", label: "Replay", count: counts.replay },
  ];

  const filteredStreams = useMemo(() => {
    switch (streamFilter) {
      case "live":
        return streamList.filter((stream) => getStatus(stream) === "live");

      case "scheduled":
        return streamList.filter((stream) => getStatus(stream) === "scheduled");

      case "replay":
        return streamList.filter(isReplayStream);

      default:
        return streamList;
    }
  }, [streamList, streamFilter]);

  const handleCardClick = (stream) => {
    const id = getStreamId(stream);

    if (!id) return;

    const status = getStatus(stream);

    if (status === "scheduled" || status === "live") {
      setLiveStreamId(id);
      return;
    }

    if (hasReplay(stream)) {
      setReplayStreamId(id);
      return;
    }

    setSelectedInfoStream(stream);
  };

  const handleSettingsClick = (stream) => {
    const id = getStreamId(stream);

    if (id) {
      setSetupStreamId(id);
    }
  };

  const handleCloseBroadcastSetup = () => {
    setSetupStreamId(null);
    void getStreamsByUsername();
  };

  const handleStreamDeleted = async () => {
    await getStreamsByUsername();
  };

  const emptyTitle =
    streamList.length === 0
      ? "No streams created yet"
      : `No ${streamFilter} streams found`;

  const emptyDescription =
    streamList.length === 0
      ? "Create a stream to get started."
      : "Try switching to another filter.";

  return (
    <ViewContainer>
      <ViewHeader>
        <Heading
          title="My Streams"
          size="sm"
          subTitle="Manage your live streams and replays."
        />
      </ViewHeader>

      <div className="creator-streams-view">
        <div className="creator-streams-view__filters">
          <SecondaryPillTabs
            tabs={streamFilterTabs}
            activeTab={streamFilter}
            onChange={setStreamFilter}
          />
        </div>

        {error && (
          <div className="creator-streams-view__error" role="alert">
            {getErrorMessage(error)}
          </div>
        )}

        {loading && streamList.length === 0 ? (
          <div className="creator-streams-view__state" role="status">
            Loading streams...
          </div>
        ) : filteredStreams.length === 0 ? (
          <div className="creator-streams-view__state">
            <h3>{emptyTitle}</h3>
            <p>{emptyDescription}</p>
          </div>
        ) : (
          <StreamVideosGrid
            streams={filteredStreams}
            selectedInfoStream={selectedInfoStream}
            getStreamId={getStreamId}
            getStatus={getStatus}
            canPlayStream={canPlayStream}
            getStreamImage={getStreamImage}
            getStreamDetailText={getStreamDetailText}
            getBadgeLabel={getBadgeLabel}
            getBadgeVariant={getBadgeVariant}
            getInfoModalTitle={getInfoModalTitle}
            getInfoModalMessage={getInfoModalMessage}
            getInfoModalMeta={getInfoModalMeta}
            onCardClick={handleCardClick}
            onSettingsClick={handleSettingsClick}
            onCloseInfoModal={() => setSelectedInfoStream(null)}
          />
        )}
      </div>

      {setupStreamId && (
        <CreatorStreamSetupForm
          streamSessionId={setupStreamId}
          onClose={handleCloseBroadcastSetup}
          onDeleted={handleStreamDeleted}
        />
      )}

      <StreamLive
        streamSessionId={liveStreamId}
        isOpen={Boolean(liveStreamId)}
        onClose={() => setLiveStreamId(null)}
      />

      <StreamReplay
        streamSessionId={replayStreamId}
        isOpen={Boolean(replayStreamId)}
        onClose={() => setReplayStreamId(null)}
      />
    </ViewContainer>
  );
}
